import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { useForm } from "../useForm";

import SelectMenu from "../../controls/SelectMenu";
import {
  convertToDefEventPara,
  makeBeepNoise,
  orderStatusOptions,
  updateObjectInArray,
  isSunDistribution,
  convertToTwoDecimals,
} from "../../../shared/utility";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import Input from "../../controls/Input";
import QuickScanAddToShippingForm from "./QuickScanAddToShippingForm";
import { Accordion, AccordionTab } from "primereact/accordion";
import OrderItemsForShippingV2 from "./OrderItemsForShippingV2";
import { Checkbox } from "primereact/checkbox";
import ExpiredItemsAccordion from "./ExpiredItemsAccordion";
import Pills from "../../controls/Pills";
import { row } from "mathjs";

const btnPrimary =
  "inline-flex items-center justify-center rounded-lg bg-main-purple px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";
const btnSecondary =
  "inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-400";
const btnDanger =
  "inline-flex items-center justify-center rounded-lg border border-rose-200 bg-white px-3.5 py-2 text-sm font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-500";

function resolveLinkedOrder(values, shipping) {
  const orderId =
    (typeof values.order_id === "object" && values.order_id?.value) ||
    values.order_id ||
    shipping?.order?.value ||
    shipping?.order_id ||
    0;

  const orderNumber =
    shipping?.order?.label ||
    (typeof values.order_id === "object" && values.order_id?.label) ||
    values.po_number ||
    (orderId ? `Order #${orderId}` : "");

  // Shipping is always tied to a regular order (fetchOrder / fetchOrderByShippingId).
  const orderHref = orderId ? `/orders/${orderId}` : null;

  return { orderId, orderNumber, orderHref };
}

const initialFValues = {
  id: 0,
  customer_id: 0,
  subtotal_dollar_amount: 0,
  total_dollar_amount: 0,
  order_id: 0,
  orderItems: [],
  po_number: "",
  sun_customer_po_number: "",
  pallet_count: "",
  total_freight_weight: "",
  appointment_number: "",
  temperature_recorder_number: "",
  shipped_subtotal_dollar_amount: 0,
  shipped_total_dollar_amount: 0,
};

export default function ShippingFormV2(props) {
  const {
    orders,
    onFetchOrderItems,
    isEdit,
    shipping,
    setEmptyModelOpen,
    showErrorModal,
    onFetchPackagedItemByBarcode,
    updateShippingApiCall,
    createShippingApiCall,
    addPackagedItemToOrderItem,
    deleteShippingItemApiCall,
    addSkidLabelToOrderItem,
    employeeOptions,
    deleteShipping,
    printShipping,
    quickAddPackagedItemToOrderItem,
    deleteQuickScanShippingItemByLot,
    exportOrderForNetSuite,
    getOrderItemsOnly,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    sunCustomers,
    sendShippingToQuickbooks,
    createSolmazInvoice,
    openSunShippingLabelReview,
    printShippingLabel,
    calculateFreightWeight,
    generateBolDocument,
    generateBolNumber,
    updateOrderItemUnitPrice,
    checkForExpiredItems,
  } = props;

  // optional setter for row details (from ViewModel) so we can update subtable state directly
  const { setRowDetails } = props;

  const [barcodeScanInProgress, setBarcodeScanInProgress] = useState(false);
  const [quickScan, setQuickScan] = useState(false);
  const [activeAccordionIndexes, setActiveAccordionIndexes] = useState(() =>
    isSunDistribution() ? [1] : [0],
  );

  const barcodeInputRef = useRef(null);

  const validate = (fieldValues = values) => {
    let temp = { ...errors };

    temp.order_id = null;
    if (values.order_id === 0) {
      temp.order_id = "Please fill out field.";
    }

    setErrors({
      ...temp,
    });

    if (fieldValues === values) {
      return Object.values(temp).every((x) => x === null);
    }
  };

  const { values, setValues, errors, setErrors, handleInputChange } = useForm(
    initialFValues,
    false,
    validate,
  );

  const { orderNumber, orderHref } = resolveLinkedOrder(values, shipping);
  const statusLabel =
    values?.status?.label ||
    orderStatusOptions.find((option) => option.value == values?.status)?.label ||
    null;
  const statusType =
    values?.status?.type ||
    orderStatusOptions.find((option) => option.value == values?.status)?.type ||
    null;

  useEffect(() => {
    if (!shipping) {
      return;
    }

    const hydrateFromShipping = (source) => ({
      ...source,
      status: orderStatusOptions.filter(
        (option) => option.value == source.status,
      )[0],
      is_prepaid: source.is_prepaid === "1" ? true : false,
    });

    // Editable fields that live in form state before shipping is patched/synced.
    // Preserve non-empty local values when a same-id shipping patch still has empty/stale copies.
    const preservableFields = [
      "sun_customer_po_number",
      "appointment_number",
      "temperature_recorder_number",
      "pallet_count",
      "chep_pallet_count",
      "total_freight_weight",
      "carrier_name",
      "seal_number",
      "notes",
      "invoice_number",
      "wfp_text",
      "wfp_load_id_number",
      "wfp_pickup_date",
      "wfp_gate_in_gate_out_times",
    ];

    setValues((prev) => {
      const hydrated = hydrateFromShipping(shipping);
      const isSameShipment = prev.id && prev.id === shipping.id;

      if (!isSameShipment) {
        return hydrated;
      }

      for (const key of preservableFields) {
        const incoming = shipping[key];
        const local = prev[key];
        const incomingEmpty = incoming == null || incoming === "";
        if (incomingEmpty && local) {
          hydrated[key] = local;
        }
      }

      // Prefer newly generated BOL number from shipping, else keep local.
      hydrated.bill_of_lading =
        shipping.bill_of_lading || prev.bill_of_lading || "";

      return hydrated;
    });
  }, [shipping]);

  function findIndexForOrderItem(product_id) {
    return values.orderItems.findIndex(
      (element) => element.product_id === product_id,
    );
  }

  const updateShipping = () => {
    if (validate()) {
      updateShippingApiCall(values);
    }
  };

  const createShipping = () => {
    if (isEdit) {
      updateShipping();
    } else {
      if (validate()) {
        createShippingApiCall(values);
      }
    }
  };

  const onCalculateFreightWeight = async () => {
    const callResult = await calculateFreightWeight(
      values.pallet_count,
      values,
    );
    if (callResult && callResult.success) {
      setValues({
        ...values,
        total_freight_weight: callResult.data.total_freight_weight,
      });
    }
  };

  const normalItemScanned = async function (packagedItem, itemIndex) {
    const orderItems = [...values.orderItems];
    const orderItem = orderItems[itemIndex];

    // const packageAlreadyScanned = orderItem.shippingItems.findIndex(
    //   (element) => {
    //     return element.id === packagedItem.id;
    //   }
    // );
    // if (packageAlreadyScanned >= 0) {
    //   showErrorModal("Item already scanned");
    //   return;
    // }

    let callResult = { success: false };
    if (isEdit) {
      callResult = await addPackagedItemToOrderItem(
        values.id,
        orderItem,
        packagedItem,
      );
    }

    if (callResult.success || !isEdit) {
      let newShippingItems = [packagedItem];
      if (orderItem.shippingItems) {
        newShippingItems = [...orderItem.shippingItems, packagedItem];
      }
      const shipped_amount = orderItem.shipped_amount
        ? parseFloat(orderItem.shipped_amount)
        : 0;

      const newAmountBeingShipped =
        orderItem.selling_unit_of_measure === "Kg"
          ? parseFloat(packagedItem.packing_size)
          : 1;

      const orderItemBeingEdited = {
        ...orderItem,
        shipped_amount: shipped_amount + newAmountBeingShipped,
        shippingItems: newShippingItems,
      };
      var payload = {
        index: itemIndex,
        item: orderItemBeingEdited,
      };
      const newOrderItems = updateObjectInArray(orderItems, payload);

      // Handle expired items
      let updatedExpiredItems = values.expiredOrderItems || [];
      let updatedHasExpired = values.hasExpiredOrderItems || false;

      if (isEdit && callResult.data?.is_expired) {
        const addedItem = callResult.data.packaged_item;
        const newExpiredItem = {
          product_name: addedItem.product_name,
          product_lot: addedItem.product_lot,
          serial_number: addedItem.serial_number,
          best_before_date: addedItem.best_before_date,
        };
        updatedExpiredItems = [...updatedExpiredItems, newExpiredItem];
        updatedHasExpired = true;
      }

      setValues({
        ...values,
        orderItems: newOrderItems,
        expiredOrderItems: updatedExpiredItems,
        hasExpiredOrderItems: updatedHasExpired,
        scanned_box_label: "",
      });
      const added_item = callResult.data.packaged_item;
      // add the new packaged item to the rowDetails map for this order item so subtable updates immediately
      if (added_item.order_item_id in rowDetails) {
        var old_packaged_items = rowDetails[added_item.order_item_id];
        old_packaged_items.push(added_item);
        setRowDetails({
          ...rowDetails,
          [added_item.order_item_id]: old_packaged_items,
        });
      }
    }
  };

  const skidLabelScanned = async function (packagedItems, itemIndex) {
    const orderItems = [...values.orderItems];
    const orderItem = orderItems[itemIndex];

    var newItems = packagedItems.packaged_item.filter(function (obj) {
      return (
        orderItem.shippingItems.findIndex((item) => item.id === obj.id) === -1
      );
    });

    let callResult = { success: false };
    if (isEdit) {
      callResult = await addSkidLabelToOrderItem(
        values.id, // this is the shipping id
        orderItem.id,
        newItems.map((item) => item.id),
      );
    }

    if (callResult.success || !isEdit) {
      let newShippingItems = [...newItems];
      if (orderItem.shippingItems) {
        newShippingItems = [...orderItem.shippingItems, ...newItems];
      }
      const shipped_amount = orderItem.shipped_amount
        ? parseInt(orderItem.shipped_amount)
        : 0;

      const orderItemBeingEdited = {
        ...orderItem,
        shipped_amount: shipped_amount + packagedItems.count,
        shippingItems: newShippingItems,
      };
      var payload = {
        index: itemIndex,
        item: orderItemBeingEdited,
      };
      const newOrderItems = updateObjectInArray(orderItems, payload);
      setValues({
        ...values,
        orderItems: newOrderItems,
        scanned_box_label: "",
      });
    }
  };

  const quickAddScannedItemAmount = async function (newValues, orderItemId) {
    const callResult = await quickAddPackagedItemToOrderItem({
      ...newValues,
      order_item_id: orderItemId,
    });
    if (callResult.success) {
      const resultOrderItems = await getOrderItemsOnly();
      if (resultOrderItems.success) {
        setEmptyModelOpen({
          childComponent: null,
          open: false,
        });

        var newExpiredItems = await checkForExpiredItems();
        setValues({
          ...values,
          orderItems: resultOrderItems.data,
          expiredOrderItems: newExpiredItems.items,
          hasExpiredOrderItems: newExpiredItems.has_expired_items,
          scanned_box_label: "",
        });
      }
    }
  };

  /*
   * The edit button will open the popup again and display the same information.
   * If its new shipping folow, on enter it will query the items again.
   * If the shipping is being edited, then it will remove all quick_scan items for that shipping and query new ones
   */
  const openQuickScanPopupWithFetchLatest = async function (item) {
    // let callResult = await onFetchPackagedItemByBarcode(item);
    const orderItemIndex = findIndexForOrderItem(item.product_id);
    openQuickScanPopup(item, orderItemIndex, item.shippingItems.length);
  };

  const openQuickScanPopup = (item, orderItemIndex, existingQuantity) => {
    const orderItems = [...values.orderItems];
    const orderItem = orderItems[orderItemIndex];

    var currQuantity = 0;
    if (existingQuantity < 1 || typeof existingQuantity === "undefined") {
    } else {
      currQuantity = existingQuantity;
    }

    setEmptyModelOpen({
      childComponent: (
        <QuickScanAddToShippingForm
          confirmAction={(newValues) =>
            quickAddScannedItemAmount(newValues, orderItem.id)
          }
          item={item}
          setOpen={setEmptyModelOpen}
          currQuantity={currQuantity}
          orderQuantity={orderItem.quantity}
          selling_unit_of_measure={orderItem.selling_unit_of_measure}
        />
      ),
      open: true,
    });
  };

  const handleBarcodeScan = async function (scannedBarcode) {
    setValues({
      ...values,
      scanned_box_label: "",
    });
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
    let callResult = await onFetchPackagedItemByBarcode(scannedBarcode);
    if (callResult.success) {
      makeBeepNoise();
      const packagedItem = callResult.data;
      var prodIdToSearch = packagedItem.packaged_item.product_id;
      if (packagedItem.is_skid_label) {
        if (packagedItem.packaged_item.length > 0) {
          prodIdToSearch = packagedItem.packaged_item[0].product_id;
        } else {
          showErrorModal("No items for skid label");
          return;
        }
      }
      const itemIndex = findIndexForOrderItem(prodIdToSearch);

      if (itemIndex >= 0) {
        if (packagedItem.is_skid_label) {
          skidLabelScanned(packagedItem, itemIndex);
        } else if (quickScan) {
          openQuickScanPopup(packagedItem.packaged_item, itemIndex);
        } else {
          normalItemScanned(packagedItem.packaged_item, itemIndex);
        }
      } else {
        showErrorModal("Item not in order");
      }
    } else {
      showErrorModal("We could not find that item.");
    }
  };

  const onDeleteQuickScannedLotItem = async function (item) {
    const product_lot = item.product_lot;
    const product_id = item.product_id;
    const order_item_id = item.order_item_id;
    const amountForLot = item.shippingItems.length;
    var onItemDeleted = { success: false };

    const itemIndex = findIndexForOrderItem(product_id);

    // Extract serial_numbers from items with this lot
    const serial_numbers = item.shippingItems.map(
      (shippingItem) => shippingItem.serial_number,
    );

    if (isEdit) {
      onItemDeleted = await deleteQuickScanShippingItemByLot({
        product_lot: product_lot,
        shipping_id: shipping.id,
        order_item_id: order_item_id,
        lot_quantity: item.shippingItems.length,
        serial_numbers: serial_numbers,
      });
    }

    if (onItemDeleted.success || !isEdit) {
      if (itemIndex >= 0) {
        const orderItems = [...values.orderItems];
        const orderItem = orderItems[itemIndex];

        const filteredItems = orderItem.shippingItems.filter((element) => {
          return element.product_lot !== product_lot;
        });
        const orderItemBeingEdited = {
          ...orderItem,
          shipped_amount: parseInt(orderItem.shipped_amount) - amountForLot,
          shippingItems: filteredItems,
        };
        var payload = {
          index: itemIndex,
          item: orderItemBeingEdited,
        };
        const newOrderItems = updateObjectInArray(orderItems, payload);
        setValues({ ...values, orderItems: newOrderItems });
        setEmptyModelOpen({
          childComponent: null,
          open: false,
        });
        // Update rowDetails map for this order item so subtable updates immediately
        setRowDetails((prev) => {
          const existing = prev || {};
          const current = existing[orderItem.id] || [];
          const updated = current.filter((element) => {
            return !serial_numbers.includes(element.serial_number);
          });
          return { ...existing, [orderItem.id]: updated };
        });
      }
    }
  };

  const makeDeleteShippingItemApiCall = async function (
    id,
    barcode,
    product_id,
    product_serial_number,
  ) {
    var onItemDeleted = { success: false };

    const itemIndex = findIndexForOrderItem(product_id);
    // Extract serial_number from the item being deleted
    let serial_number = null;
    if (itemIndex >= 0) {
      const orderItems = [...values.orderItems];
      const orderItem = orderItems[itemIndex];
      if (orderItem.shippingItems) {
        const itemToDelete = orderItem.shippingItems.find(
          (element) => element.shipping_barcode === barcode,
        );
        if (itemToDelete) {
          serial_number = itemToDelete.serial_number;
        }
      }
    }

    if (isEdit) {
      onItemDeleted = await deleteShippingItemApiCall(id, serial_number);
    }
    if (onItemDeleted.success || !isEdit) {
      if (itemIndex >= 0) {
        const orderItems = [...values.orderItems];
        const orderItem = orderItems[itemIndex];

        // remove from expired items if exists
        let updatedExpiredItems = values.expiredOrderItems || [];
        updatedExpiredItems = updatedExpiredItems.filter((element) => {
          return element.serial_number !== product_serial_number;
        });

        const orderItemBeingEdited = {
          ...orderItem,
          shipped_amount: parseInt(orderItem.shipped_amount) - 1,
        };
        var payload = {
          index: itemIndex,
          item: orderItemBeingEdited,
        };
        const newOrderItems = updateObjectInArray(orderItems, payload);
        setValues({
          ...values,
          expiredOrderItems: updatedExpiredItems,
          orderItems: newOrderItems,
        });
        setEmptyModelOpen({
          childComponent: null,
          open: false,
        });

        // Update rowDetails map for this order item so subtable removes the deleted item immediately
        setRowDetails((prev) => {
          const existing = prev || {};
          const current = existing[orderItem.id] || [];
          const updated = current.filter((element) => {
            return element.shipping_barcode !== barcode;
          });
          return { ...existing, [orderItem.id]: updated };
        });
      }
    }
  };

  const askDeleteShippingItem = function (item) {
    let text = item.product_name;
    if (item.is_lot) {
      text = item.product_lot;
    }

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => {
            if (item.is_lot) {
              onDeleteQuickScannedLotItem(item);
            } else {
              makeDeleteShippingItemApiCall(
                item.id,
                item.shipping_barcode,
                item.product_id,
                item.serial_number,
              );
            }
          }}
          message={`Are you sure you want to delete ${text}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const askDeleteShipping = () => {
    const customerName = values.customer?.label || values.customer || "this customer";
    const poNumber = values.po_number || "—";

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={deleteShipping}
          message={`Are you sure you want to delete shipping for ${customerName}, PO #: ${poNumber}?`}
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  return (
    <div className="space-y-4 h-full pb-4">
      <div className="card-custom px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
              Shipment
            </p>
            <h1 className="truncate text-xl font-semibold text-slate-900">
              {orderNumber || "Untitled shipment"}
            </h1>
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center">
              <span className="inline-flex w-full items-center rounded-md bg-slate-100 px-2.5 py-1 text-md md:text-xs font-medium text-slate-700 sm:w-auto">
                PO # {values.po_number || "—"}
              </span>
              {statusLabel ? (
                <Pills content={{ text: statusLabel, type: statusType }} />
              ) : null}
            </div>
          </div>
          {orderHref ? (
            <Link to={orderHref} target="_blank" rel="noreferrer" className={btnPrimary}>
              View Order
            </Link>
          ) : null}
        </div>
      </div>

      {values?.hasExpiredOrderItems &&
        values?.expiredOrderItems?.length > 0 && (
          <ExpiredItemsAccordion
            expiredOrderItems={values.expiredOrderItems}
          />
        )}

      <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-3">
        <div className="order-2 space-y-4 sm:order-1 sm:col-span-2">
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-3 sm:p-4">
            <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-4">
              <div className="sm:col-span-3">
                <label
                  htmlFor="scanned_box_label"
                  className="mb-1.5 block text-sm font-medium text-slate-900"
                >
                  Scan barcode
                </label>
                <input
                  id="scanned_box_label"
                  placeholder="Scan box label"
                  name="scanned_box_label"
                  disabled={barcodeScanInProgress}
                  ref={barcodeInputRef}
                  onChange={handleInputChange}
                  value={values.scanned_box_label}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleBarcodeScan(e.target.value);
                  }}
                  type="search"
                  className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm shadow-sm placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:bg-slate-100"
                />
              </div>
              <label
                htmlFor="quick_scan"
                className="flex cursor-pointer items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5 sm:min-h-[42px]"
              >
                <input
                  id="quick_scan"
                  name="quick_scan"
                  type="checkbox"
                  checked={Boolean(quickScan)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  onChange={(e) => setQuickScan(e.target.checked)}
                />
                <span>
                  <span className="block text-sm font-medium text-slate-900">
                    Quick Scan
                  </span>
                </span>
              </label>
            </div>
          </div>

          <OrderItemsForShippingV2
            askDeleteShippingItem={askDeleteShippingItem}
            order={values}
            openQuickScanPopupWithFetchLatest={openQuickScanPopupWithFetchLatest}
            rowDetails={rowDetails}
            fetchRowDetails={fetchRowDetails}
            loadingRows={loadingRows}
            openSunShippingLabelReview={openSunShippingLabelReview}
            printShippingLabel={printShippingLabel}
            updateOrderItemUnitPrice={updateOrderItemUnitPrice}
            setOrderItems={(updatedOrderItems) =>
              setValues({ ...values, orderItems: updatedOrderItems })
            }
          />
        </div>

        <div className="order-1 space-y-4 sm:order-2">
          <Accordion
            multiple
            activeIndex={activeAccordionIndexes}
            onTabChange={(event) => setActiveAccordionIndexes(event.index)}
          >
            <AccordionTab header="Shipping Information">
              <div className="space-y-3 p-1 pt-2">
                <SelectMenu
                  handleInputChange={(e) => {
                    handleInputChange(
                      convertToDefEventPara("order_id", e.target.value),
                    );
                    onFetchOrderItems(e.target.value.value);
                  }}
                  name="order_id"
                  options={orders}
                  defaultValue={isEdit ? shipping.order : null}
                  title={"Order to ship"}
                  disabled={isEdit}
                  error
                />

                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="created_by"
                  options={employeeOptions}
                  defaultValue={isEdit ? shipping.created_by : null}
                  title={"Employee"}
                  error={errors.created_by}
                  showLabel={true}
                />

                <Input
                  label="Invoice #"
                  name="invoice_number"
                  onChange={handleInputChange}
                  value={values.invoice_number}
                  labelOn={true}
                  type="text"
                  autoComplete="off"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.invoice_number}
                />

                <Input
                  label="Seal #"
                  name="seal_number"
                  onChange={handleInputChange}
                  value={values.seal_number}
                  labelOn={true}
                  type="text"
                  autoComplete="off"
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                  error={errors.seal_number}
                />

                <SelectMenu
                  handleInputChange={handleInputChange}
                  name="status"
                  options={orderStatusOptions.filter(
                    (option) => option.value !== -1,
                  )}
                  value={values.status}
                  title={"Status"}
                  error={errors.status}
                  showLabel={true}
                />

                <div className="space-y-3 border-t border-slate-200 pt-4">
                  <button
                    type="button"
                    className={`${btnPrimary} w-full`}
                    onClick={createShipping}
                  >
                    {values.id > 0 ? "Save" : "Submit"}
                  </button>

                  <div className="flex flex-wrap gap-2">
                    {values.id > 0 && (
                      <button
                        type="button"
                        className={btnSecondary}
                        onClick={() => printShipping(values)}
                      >
                        Print
                      </button>
                    )}

                    {values.id > 0 && (
                      <button
                        type="button"
                        className={btnSecondary}
                        onClick={exportOrderForNetSuite}
                      >
                        Export
                      </button>
                    )}

                    {values.id > 0 && !isSunDistribution() && (
                      values.solmaz_quickbooks_invoice_id ? (
                        <a
                          href={`https://qbo.intuit.com/app/invoice?txnId=${values.solmaz_quickbooks_invoice_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className={btnSecondary}
                        >
                          View Invoice
                        </a>
                      ) : (
                        <button
                          type="button"
                          className={btnSecondary}
                          onClick={() => createSolmazInvoice(values)}
                        >
                          Create Invoice
                        </button>
                      )
                    )}
                  </div>

                  {values.id > 0 && !isSunDistribution() && (
                    <button
                      type="button"
                      className={btnDanger}
                      onClick={askDeleteShipping}
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            </AccordionTab>

            {isSunDistribution() && (
              <AccordionTab header="Sun Distribution">
                <div className="space-y-3 p-1 pt-2">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Sun Distribution Details
                    </h3>
                    <p className="text-xs text-slate-500">
                      Shipment information and QuickBooks actions.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <SelectMenu
                      handleInputChange={handleInputChange}
                      name="sun_customer"
                      options={sunCustomers}
                      value={values.sun_customer}
                      title={"SUN Customer"}
                      error={errors.sun_customer}
                      showLabel={true}
                      parentClass="col-span-2"
                    />

                    <Input
                      label="Customer PO Number"
                      name="sun_customer_po_number"
                      tooltip="You need to enter manually."
                      onChange={handleInputChange}
                      value={values.sun_customer_po_number}
                      labelOn={true}
                      type="text"
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />

                    <div className="space-y-1">
                      <Input
                        label="BOL Number"
                        name="bill_of_lading"
                        tooltip="Will be generated automatically."
                        onChange={handleInputChange}
                        value={values.bill_of_lading}
                        labelOn={true}
                        type="text"
                        autoComplete="off"
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                      />
                      <button
                        type="button"
                        className={`${btnPrimary} !py-1.5`}
                        onClick={() => {
                          generateBolNumber();
                        }}
                      >
                        Generate BOL Number
                      </button>
                    </div>

                    <Input
                      label="Appointment Number"
                      name="appointment_number"
                      tooltip="You need to enter manually."
                      onChange={handleInputChange}
                      value={values.appointment_number}
                      labelOn={true}
                      type="text"
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />

                    <Input
                      label="Pallet Count"
                      name="pallet_count"
                      tooltip="Will be used to calculate total weight."
                      onChange={handleInputChange}
                      value={values.pallet_count}
                      labelOn={true}
                      type="number"
                      min={0}
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />

                    <Input
                      label="Chep Pallet Count"
                      name="chep_pallet_count"
                      onChange={handleInputChange}
                      value={values.chep_pallet_count}
                      labelOn={true}
                      type="number"
                      min={0}
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />

                    <Input
                      label="Carrier Name"
                      name="carrier_name"
                      onChange={handleInputChange}
                      value={values.carrier_name}
                      labelOn={true}
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />

                    <Input
                      label="Total Freight Weight"
                      name="total_freight_weight"
                      onChange={handleInputChange}
                      value={values.total_freight_weight}
                      labelOn={true}
                      type="text"
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />

                    <Input
                      label="Temperature Recorder Number"
                      name="temperature_recorder_number"
                      onChange={handleInputChange}
                      value={values.temperature_recorder_number}
                      labelOn={true}
                      type="text"
                      autoComplete="off"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />

                    <Input
                      label="Sales Rep"
                      name="sun_sales_rep_name"
                      onChange={handleInputChange}
                      value={values.sun_sales_rep_name}
                      labelOn={true}
                      disabled={true}
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <div className="flex items-center">
                      <Checkbox
                        inputId="is_prepaid"
                        name="is_prepaid"
                        value="Is Prepaid"
                        onChange={(e) =>
                          setValues({ ...values, is_prepaid: e.checked })
                        }
                        checked={values.is_prepaid}
                      />
                      <label htmlFor="is_prepaid" className="ml-2 text-sm text-slate-700">
                        Is Prepaid
                      </label>
                    </div>

                    {!values.is_prepaid ? (
                      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Input
                          label="WFP"
                          name="wfp_text"
                          onChange={handleInputChange}
                          value={values.wfp_text}
                          labelOn={true}
                          type="text"
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        />

                        <Input
                          label="WFP Load ID Number"
                          name="wfp_load_id_number"
                          onChange={handleInputChange}
                          value={values.wfp_load_id_number}
                          labelOn={true}
                          type="text"
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        />

                        <Input
                          label="WFP Pickup Date"
                          name="wfp_pickup_date"
                          onChange={handleInputChange}
                          value={values.wfp_pickup_date}
                          labelOn={true}
                          type="text"
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        />

                        <Input
                          label="WFP Gate In/Out Times"
                          name="wfp_gate_in_gate_out_times"
                          onChange={handleInputChange}
                          value={values.wfp_gate_in_gate_out_times}
                          labelOn={true}
                          type="text"
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm"
                        />
                      </div>
                    ) : null}
                  </div>

                  <div className="space-y-3 border-t border-slate-200 pt-4">
                    <button
                      type="button"
                      className={`${btnPrimary} w-full`}
                      onClick={createShipping}
                    >
                      {values.id > 0 ? "Save" : "Submit"}
                    </button>

                    <div className="flex flex-wrap gap-2">
                      {values.id > 0 && (
                        <button
                          type="button"
                          className={btnSecondary}
                          onClick={() => printShipping(values)}
                        >
                          Print
                        </button>
                      )}

                      {values.id > 0 && (
                        <>
                          {values.sun_quickbooks_po_id > 0 ? (
                            <>
                              <a
                                href={`https://qbo.intuit.com/app/purchaseorder?txnId=${values.sun_quickbooks_po_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className={btnSecondary}
                              >
                                View PO
                              </a>
                              <a
                                href={`https://qbo.intuit.com/app/invoice?txnId=${values.sun_quickbooks_invoice_id}`}
                                target="_blank"
                                rel="noreferrer"
                                className={btnSecondary}
                              >
                                View Invoice
                              </a>
                            </>
                          ) : (
                            <button
                              type="button"
                              className={btnSecondary}
                              onClick={() => sendShippingToQuickbooks(values)}
                            >
                              Send to QB
                            </button>
                          )}
                          <button
                            type="button"
                            className={btnSecondary}
                            onClick={onCalculateFreightWeight}
                          >
                            Calculate Weight
                          </button>
                          <button
                            type="button"
                            className={btnSecondary}
                            onClick={() => generateBolDocument(values)}
                          >
                            BOL
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </AccordionTab>
            )}
          </Accordion>
        </div>
      </div>
    </div>
  );
}
